<template>
  <div>
    <div class="text-h5">Organization</div>
    <q-tabs
      v-if="isAdmin"
      id="intercom-tabs"
      v-model="activeTab"
      class="q-mt-md"
      align="left"
      :style="`min-height: inherit; border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
      no-caps
      inline-label
      dense
      mobile-arrows
      outside-arrows
      @update:model-value="(tab) => $tracker.changedTab(activeTab, tab, 'organization')"
    >
      <q-tab
        v-for="tab in tags"
        :id="`intercom-tabs-${tab}`"
        :key="tab.value"
        :ripple="false"
        :name="tab.value"
        :label="tab.label"
        :icon="tab.icon"
      />
    </q-tabs>
    <q-separator v-else class="q-my-md" />
    <div v-if="!organizationLoading">
      <div class="q-gutter-y-md q-mt-lg">
        <template v-if="isAdmin">
          <div v-show="activeTab === 'general'" class="q-gutter-y-md">
            <organization-name />
            <organization-members />
            <user-licenses v-if="billedPerSeat" />
            <organization-logo />
          </div>
          <div v-show="activeTab === 'report-customization'" class="q-gutter-y-md">
            <report-access v-if="organization?.products?.length" />
            <organization-sections
              v-if="organization"
              :organization="organization"
              :save-changes-loading="savingOrganizationSections"
              @save="saveOrganizationSections"
            />
            <report-tags />
          </div>
          <apis-services v-show="activeTab === 'apis-services'" />
        </template>
        <template v-else>
          <q-card flat bordered>
            <div class="q-pa-md justify-between">
              <div class="text-h6 q-my-auto">
                Unauthorized <q-icon name="do_disturb" color="accent" />
              </div>
            </div>
            <q-separator />
            <q-card-section class="q-gutter-y-lg">
              <div>
                The Organization tab is designed to give your company's administrators the
                information and tools necessary to effectively manage your HelloData account. It
                includes bulk settings for user management, report customization, API integration,
                troubleshooting, and billing. If you need access to these settings, please reach out
                to your company's designated HelloData admin.
                <div class="q-mt-md">
                  To view your properties,
                  <router-link :to="{ name: 'Dashboard' }">click here</router-link> to return to
                  your Dashboard. If you need Admin access to
                  <b>{{ organization?.display_name || 'Unknown' }}</b
                  >, please contact
                  <b>{{
                    organizationMembers.find((_) => _.id === organization?.owner_user_id)?.email ||
                    'Unknown'
                  }}</b>
                  to request permissions.
                </div>
              </div>
            </q-card-section>
          </q-card>
        </template>
        <template v-if="activeTab === 'general'">
          <q-btn
            v-if="!isPrimaryOwner"
            color="red"
            outline
            no-caps
            unelevated
            @click="leaveOrganizationDialog = true"
          >
            Leave Organization
          </q-btn>
          <q-card v-if="subscriptionCancelAt && isPrimaryOwner" flat bordered>
            <q-card-section class="row justify-between">
              <div class="q-my-auto">
                Your subscription is scheduled to end on <b>{{ subscriptionCancelAt }}</b
                >.
              </div>
              <q-btn
                color="primary"
                outline
                no-caps
                unelevated
                :loading="renewLoading"
                @click="renewSubscriptionDialog = true"
              >
                Renew Plan
              </q-btn>
            </q-card-section>
          </q-card>
          <q-btn
            v-else-if="propertyReportSubscription && isPrimaryOwner"
            color="red"
            outline
            no-caps
            unelevated
            :loading="cancelLoading"
            @click="cancelSubscriptionDialog = true"
          >
            Cancel Subscription
          </q-btn>
        </template>
        <q-dialog v-model="leaveOrganizationDialog" transition-duration="100">
          <q-card style="width: 400px">
            <q-card-section class="row items-center">
              <div class="q-ml-sm">
                <p>
                  Are you sure you want to leave <b>{{ organization?.display_name }}</b
                  >?
                </p>
                <p>
                  You will lose access to your work unless you join a new organization or subscribe
                  yourself.
                </p>
              </div>
            </q-card-section>
            <q-card-actions align="right">
              <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
              <q-btn
                v-close-popup
                no-caps
                unelevated
                label="Leave"
                color="red"
                @click="leaveOrganization"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
        <q-dialog v-model="cancelSubscriptionDialog" transition-duration="100">
          <q-card style="width: 400px">
            <q-card-section class="row items-center">
              <div class="text-h6">Cancel Plan</div>
              <div class="q-mt-sm">
                Your subscription will remain active until the end of the current period, after
                which it will be canceled.
              </div>
            </q-card-section>
            <q-card-actions align="right">
              <q-btn v-close-popup flat label="Stay Subscribed" no-caps unelevated />
              <q-btn
                v-close-popup
                no-caps
                unelevated
                label="Cancel Subscription"
                color="red"
                @click="cancelSubscription"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
        <q-dialog v-model="renewSubscriptionDialog" transition-duration="100">
          <q-card style="width: 400px">
            <q-card-section class="row items-center">
              <div class="text-h6">Renew Plan</div>
              <div class="q-mt-sm">
                This plan will no longer be canceled. It will renew on {{ subscriptionCancelAt }}.
              </div>
            </q-card-section>
            <q-card-actions align="right">
              <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
              <q-btn
                v-close-popup
                no-caps
                unelevated
                label="Renew Plan"
                color="primary"
                @click="renewSubscription"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useUserStore } from '@/stores/user'
import { useStripeStore } from '@/stores/stripe'
import OrganizationName from '@/components/Organization/OrganizationName.vue'
import OrganizationMembers from '@/components/Organization/OrganizationMembers.vue'
import OrganizationLogo from '@/components/Organization/OrganizationLogo.vue'
import ReportAccess from '@/components/Organization/ReportAccess.vue'
import ReportTags from '@/components/Organization/ReportTags.vue'
import UserLicenses from '@/components/Organization/UserLicenses.vue'
import ApisServices from '@/components/Organization/ApisServices.vue'
import { format, parseISO } from 'date-fns'
import OrganizationSections from '@/components/Organization/OrganizationSections.vue'
import { type OrganizationAttributes, Section, SectionColumn } from 'common'

export default {
  name: 'SettingsOrganizationView',
  components: {
    OrganizationSections,
    OrganizationName,
    OrganizationMembers,
    OrganizationLogo,
    ReportAccess,
    ReportTags,
    UserLicenses,
    ApisServices
  },
  props: {
    urlTab: {
      type: String,
      default: 'general'
    }
  },
  data() {
    return {
      tags: [
        { label: 'General', value: 'general', icon: 'settings' },
        { label: 'Report Customization', value: 'report-customization', icon: 'dashboard' },
        { label: 'APIs & Services', value: 'apis-services', icon: 'code' }
      ],
      leaveOrganizationDialog: false,
      cancelSubscriptionDialog: false,
      renewSubscriptionDialog: false,
      cancelLoading: false,
      renewLoading: false,
      savingOrganizationSections: false
    }
  },
  computed: {
    activeTab: {
      get() {
        return this.urlTab || 'general'
      },
      set(urlTab: string) {
        if (urlTab === 'general') {
          this.$router.push({ name: 'Organization', params: { urlTab: null } })
        } else {
          this.$router.push({ name: 'Organization', params: { urlTab } })
        }
      }
    },
    isPrimaryOwner() {
      // is the actual owner of the organization in the DB
      return this.$auth0.user.value?.sub === this.organization?.owner_user_id
    },
    isAdmin() {
      return useUserStore().isAdmin
    },
    organization(): OrganizationAttributes | null {
      return useUserStore().organization
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    organizationLoading() {
      return useUserStore().organizationLoading
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    propertyReportSubscription() {
      return useStripeStore().propertyReportSubscription
    },
    subscriptionCancelAt() {
      const cancelAt = this.propertyReportSubscription?.cancel_at
      if (cancelAt) {
        return format(parseISO(cancelAt), 'MMMM d, y')
      }
      return null
    }
  },
  methods: {
    leaveOrganization() {
      const userId = this.$auth0.user.value?.sub
      if (userId && !this.isPrimaryOwner) {
        useUserStore()
          .removeFromOrganization(userId)
          .then(() => {
            useUserStore().initOrganizationMembers()
            useUserStore().initOrganizationReportAccesses()
            useUserStore().initOrganization()
            useUserStore().initPeopleInOrgOrShared()
            useStripeStore().initCustomerSubscriptions()
            this.$auth0.getAccessTokenSilently({ cacheMode: 'off' }).then(() => {})
          })
      }
    },
    cancelSubscription() {
      this.cancelLoading = true
      useStripeStore()
        .cancelSubscription()
        .then(() => {
          useStripeStore().initCustomerSubscriptions()
        })
        .finally(() => {
          this.cancelLoading = false
        })
    },
    renewSubscription() {
      this.renewLoading = true
      useStripeStore()
        .renewSubscription()
        .then(() => {
          useStripeStore().initCustomerSubscriptions()
        })
        .finally(() => {
          this.renewLoading = false
        })
    },
    saveOrganizationSections(visibleSections: Section[], visibleColumns: SectionColumn[]) {
      this.savingOrganizationSections = true
      const org = this.organization
      if (org && org?.id) {
        useUserStore()
          .updateOrganization(org.id, {
            display_name: org.display_name,
            owner_user_id: org.owner_user_id,
            admin_user_ids: org.admin_user_ids,
            connection_id: org.connection_id,
            logo_url: org.logo_url,
            report_tags: org.report_tags,
            use_security_groups: org.use_security_groups,
            sections: visibleSections,
            columns: visibleColumns
          })
          .then(() => {
            this.$q.notify({
              message: 'Changes saved.',
              color: 'positive',
              position: 'bottom'
            })
            this.$tracker.changedVisibleSections(visibleSections)
            this.$tracker.changedVisibleColumns(visibleColumns)
          })
          .catch((e) => {
            console.error(e)
            this.$q.notify({
              message: 'Error updating organization settings, please try again later.',
              color: 'negative',
              position: 'bottom'
            })
          })
          .finally(() => {
            this.savingOrganizationSections = false
          })
      }
    }
  }
}
</script>
